import React, { createContext, useCallback, useContext, useState } from 'react';
import { ADMIN_URL } from '../constants';
import { loginEmail, recoverPassword, newPassword } from '../services/login';
import { useToast } from './toast';
import { encrypt } from '../utils/encryption';

interface SignInProps {
  email?: string;
  cpf?: string;
  password?: string;
}

interface AuthContextData {
  user: SignInProps;
  loading: boolean;
  step: string;
  handleNewPassword(userData: any): void;
  handleRecoverKey(): void;
  handleLogin(userData: any, isCpf?: boolean): void;
  handleStep(step: string): void;
  handleLoading(loading: boolean): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [userData, setUserData] = useState<SignInProps>({} as SignInProps);
  const [step, setStep] = useState('fillEmail');
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const handleStep = useCallback((currentStep: string) => {
    setStep(currentStep);

    if (currentStep === 'fillEmail') {
      setUserData({ email: '', cpf: '' });
    }
  }, []);

  const handleLoading = useCallback((value) => {
    setLoading(value);
  }, []);

  const enconderUserData = (dataToEncode: unknown) => {
    const encryptedLogin = encrypt(dataToEncode);
    const redirectUrl = `${ADMIN_URL}/home?login=${encodeURIComponent(encryptedLogin || '')}`;
    window.open(redirectUrl, '_self');
  };

  const handleNewPassword = useCallback(
    (data) => {
      const user = { ...userData, ...data };
      setUserData(user);
      handleLoading(true);
      newPassword(user).then(
        () => {
          handleLoading(false);
          enconderUserData(user);
        },
        () => {
          handleLoading(false);
          addToast({
            type: 'error',
            title: 'Não foi possível cadastrar nova senha',
            description: 'Entre em contato com o suporte.',
          });
        },
      );
    },
    [userData, newPassword, handleLoading, encrypt, addToast],
  );

  const handleRecoverKey = useCallback(() => {
    const { email } = userData;
    handleLoading(true);

    recoverPassword({ email }).then(
      () => {
        handleLoading(false);
        addToast({
          type: 'success',
          title: 'Email de recuperação de senha enviado',
          description: 'Acesse o seu email e siga as instruções.',
        });
      },
      () => {
        handleLoading(false);
        addToast({
          type: 'error',
          title: 'Não foi possível enviar email',
          description: 'Tente novamente mais tarde.',
        });
      },
    );
  }, [userData, handleLoading, recoverPassword, addToast]);

  const handleLogin = useCallback(
    (data, isCpf?) => {
      let userFormatted = data;
      if (step === 'fillEmail') {
        if (isCpf) {
          userFormatted = { cpf: data.cpf };
        } else {
          userFormatted = { email: data.email };
        }
      } else {
        userFormatted = { ...userData, ...data };
      }

      setUserData(userFormatted);
      handleLoading(true);
      loginEmail(userFormatted).then(
        (res) => {
          handleLoading(false);
          const { statusCode, message } = res.data;
          if (statusCode === 200) {
            if (message === 'First Access') {
              handleStep('firstAccess');
            } else {
              enconderUserData(userFormatted);
            }
          }

          if (statusCode === 401) {
            if (!userFormatted.password) {
              setStep('fillPassword');
            } else {
              addToast({
                type: 'error',
                title: 'Senha Incorreta',
                description: 'Tente novamente. Verifique se o capslock está ativado.',
              });
            }
          }

          if (statusCode === 404) {
            addToast({
              type: 'error',
              title: 'Usuário não encontrado',
              description: 'Não encontramos seu usuário. Entre em contato com o administrativo/RH da sua empresa.',
            });
          }
        },
        () => {
          addToast({
            type: 'error',
            title: 'Não foi possível realizar login',
            description: 'Você pode ter excedido o limite de tentativas. Aguarde alguns minutos e tente novamente.',
          });
        },
      );
    },
    [userData, step, loginEmail, handleLoading, addToast, enconderUserData, handleStep],
  );

  return (
    <AuthContext.Provider
      value={{
        loading,
        user: userData,
        step,
        handleNewPassword,
        handleLoading,
        handleStep,
        handleLogin,
        handleRecoverKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside of AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
